/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocalizationStore } from '@/stores'
import { factory } from '../../../../factory-api/factory'
import { ref } from 'vue'
import { defineStore } from 'pinia'
import { convertPropertyNames } from '@/application/helpers/convertPropNames'
import { httpService } from '@/services/HttpService'
import i18n from '@/main'
import { useNavigationStore } from '../component-stores/navigation'
import { Capacitor } from '@capacitor/core'
import { setTheme } from '@/application/plugins/tailwindPlugin'
import { Theme } from '@/interfaces/theme'
import { useLanguageStore } from '@/stores/portal-stores/general-stores/language'
import { useModuleStore } from '../view-stores/module'
import { type INativeWrongLang } from '@/interfaces/helpers/ILanguage'
import { type IBreadcrumbConfig } from '@/interfaces/helpers/IBreadcrumbConfig'
import type { DeepLinkSettings } from '../../../interfaces/IDeepLinkSettings'
import type { CandidateMessages } from '../../../interfaces/ICandidateMessages'
import type { INavigationHeader } from '@/generated-api'
import type { SiteContent } from '@/generated-api'
import { resetScrollToItem } from '../../../application/helpers/utilities'

export const useAppStore = defineStore('app', () => {
  const localizationStore = useLocalizationStore()
  const navigationStore = useNavigationStore()
  const languageStore = useLanguageStore()
  const moduleStore = useModuleStore()

  const initialized = ref<boolean>(false)
  const apiUrl = ref<string>('')
  const showContentSpinner = ref<boolean>(false)
  const content = ref()
  const contentStatus = ref<number>(200)
  const siteContent = ref<SiteContent>({})
  const defaultTheme: Theme = Theme.BASE
  const theme = ref<Theme>(defaultTheme)
  const showLanguageSelector = ref<boolean>(false)
  const showNotificationCenter = ref<boolean>(false)

  const showAppSpinner = ref<boolean>(true)
  const navigationBetweenModules = ref<boolean>(true)
  const lastestRequestId = ref<number>(0)
  const nativeLinkIsAnotherLanguage = ref<INativeWrongLang | undefined>()
  const breadcrumbs = ref<IBreadcrumbConfig[]>([])
  const showNativeNavWarning = ref<boolean>(false)
  const deepLinkSettings = ref({} as DeepLinkSettings)
  const isDeeplink = ref<boolean>(false)
  const scrollHitsElement = ref<boolean>(false)
  const candidateMessages = ref<CandidateMessages[]>([])
  const callGetContent = ref<boolean>(true)
  const showValidationPage = ref<boolean>(false)
  const innerWidth = ref<number>(window.innerWidth)
  const canCloseApp = ref<boolean>(true)

  const setInitialized = async (data: boolean) => {
    // Get chosenLanguageUrl from localstorage and check if it exists, set it as the current apiUrl because its the users chosen language
    const chosenLanguageUrl =
      Capacitor.isNativePlatform() === true ? localStorage.getItem('chosenLanguageUrl') : undefined

    if (chosenLanguageUrl != undefined) {
      console.log('chosenLanguageUrl ', chosenLanguageUrl)
      apiUrl.value = chosenLanguageUrl
      const chosenLanguageLocale = localStorage.getItem('appLanguageLocale')

      if (chosenLanguageLocale != undefined) {
        languageStore.setCurrentLocale(chosenLanguageLocale)
      }
    } else if (Capacitor.isNativePlatform()) {
      await languageStore.setApiBasedOnLanguage()
    } else {
      languageStore.setApiBasedOnUrl()
    }

    await getSiteContent()

    document.title = siteContent.value.title ?? ''

    // Update the translations in the localization instance
    if (siteContent.value.translationKeys != undefined) {
      await localizationStore.updateTranslations(siteContent.value.translationKeys)
      // Sending local - as needed - but we dont use it - ignore it
      i18n.global.setLocaleMessage('en', siteContent.value.translationKeys)
    }

    initialized.value = data
  }

  const getSiteContent = async () => {
    try {
      const contentApi = await factory.getContentApi()

      const response = await contentApi.getSiteContent({ host: window.location.hostname })
      siteContent.value = response

      setSettings()

      if (Capacitor.isNativePlatform()) {
        response.nav = filterMenu(response.nav)
      }

      if (response.nav) {
        navigationStore.setNavigation(await convertPropertyNames(response.nav))
      }
      if (response.secondaryNav) {
        navigationStore.setSecondaryNavigation(response.secondaryNav)
      }
    } catch (error) {
      console.error('GetSiteContent failed with the following error: ', error)
    }
  }

  const filterMenu = (nav: INavigationHeader[] | undefined): INavigationHeader[] | undefined => {
    if (nav == undefined) {
      return undefined
    }

    const navFiltered = [] as INavigationHeader[]

    nav.forEach((n) => {
      if (n.inNative) {
        const items = n.navigationItems?.filter((i) => i.inNative === true)
        navFiltered.push({
          id: n.id,
          header: n.header,
          url: n.url,
          type: n.type,
          icon: n.icon,
          navigationItems: items,
          useModern: n.useModern,
          inNative: n.inNative
        })
      }
    })

    return navFiltered
  }

  const setSettings = () => {
    theme.value = setTheme(siteContent.value.settings?.theme)

    if (siteContent.value.frontpageSettings?.showLanguageSelector !== undefined) {
      showLanguageSelector.value = siteContent.value.frontpageSettings?.showLanguageSelector
    }

    if (siteContent.value.frontpageSettings?.showNotificationCenter !== undefined) {
      showNotificationCenter.value = siteContent.value.frontpageSettings?.showNotificationCenter
    }

    if (siteContent.value.frontpageSettings?.navigationBetweenModules !== undefined) {
      navigationBetweenModules.value = siteContent.value.frontpageSettings?.navigationBetweenModules
    }

    if (siteContent.value.frontpageSettings?.moduleCompactView !== undefined) {
      moduleStore.setCompactView(siteContent.value.frontpageSettings?.moduleCompactView)
    }

    if (siteContent.value.frontpageSettings?.automaticModuleCompletion !== undefined) {
      moduleStore.setAutomaticModuleCompletion(
        siteContent.value.frontpageSettings?.automaticModuleCompletion
      )
    }

    if (
      siteContent.value.settings?.loginBackgroundUrl &&
      siteContent.value.settings !== undefined
    ) {
      // Set API url to the relative path
      siteContent.value.settings.loginBackgroundUrl =
        apiUrl.value + siteContent.value.settings?.loginBackgroundUrl
    }

    if (siteContent.value.translationKeys !== undefined) {
      localizationStore.updateTranslations(siteContent.value.translationKeys)
      // Sending local - as needed - but we dont use it - ignore it
      i18n.global.setLocaleMessage('en', siteContent.value.translationKeys)
    }
  }

  //This do not use the factory because contentService is not a part of the swagger json
  //And also this get content dynamically
  // CONTENTSERVICE
  const getContent = async (urlName: string) => {
    console.log('CONTENTSERVICE getContent: ', urlName)

    if (!urlName.includes('notfound')) {
      //When a user navigates between pages fast, there is a chance that content is wrong because of timing in api calls.
      //To stop this we always saves content from the fastest api call, that means if a user starts the /calender api call by visitng that page, but regets and go back to /modules
      //We are ignoring the first /calendar call, and only saves data from the later call.
      //Thats why we save the date for every request
      const requestId = Date.now()
      lastestRequestId.value = requestId

      try {
        setContentSpinner(true)
        const chosenLanguageUrl = localStorage.getItem('chosenLanguageUrl')

        if (Capacitor.isNativePlatform()) {
          if (chosenLanguageUrl != undefined) {
            apiUrl.value = chosenLanguageUrl
          } else {
            await languageStore.setApiBasedOnLanguage()
          }
        }

        const url = `${apiUrl.value}/contentservice${urlName}`
        const response = await httpService.get(url)

        contentStatus.value = response.status
        console.log('getContent Response', response)
        if (contentStatus.value === 200) {
          try {
            const jsonData = await response.json()

            if (lastestRequestId.value == requestId) {
              setContent(convertPropertyNames(jsonData))
            }
          } catch (error) {
            console.error('Error parsing JSON:', error)
            // Handle the error, e.g., set content to a default value or show an error message.
          }

          const appLanguageUrl = localStorage.getItem('appLanguageUrl')
          console.log('appLanguageUrl', appLanguageUrl)
          console.log('CONTENT', content.value)
        } else if (contentStatus.value === 404) {
          setContent(undefined)
        } else if (response.Id !== null) {
          setContent(convertPropertyNames(response))
        }
      } catch (error) {
        console.error('Error in getContent:', error)
        //This should not be 404 but catch all errors on contentservice
        contentStatus.value = 404
      } finally {
        if (lastestRequestId.value == requestId) {
          setContentSpinner(false)
        }
      }
    } else {
      contentStatus.value = 404
      setContentSpinner(false)
    }
  }

  const getCandidateMessages = async (meetingId: string) => {
    candidateMessages.value = []
    const personalDataApi = await factory.getPersonalDataApi()
    const cancellationResponse = await personalDataApi.getCandidateMessages({
      meetingId: meetingId
    })

    cancellationResponse.forEach((element) => {
      candidateMessages.value.push({
        candidateID: element.candidateID,
        dateCreated: element.dateCreated,
        meetingID: element.meetingID,
        message: element.message
      })
    })
  }

  const setContentSpinner = (val: boolean) => {
    showContentSpinner.value = val
  }

  const setContentStatus = (status: any) => {
    contentStatus.value = status
  }

  const setContent = (value: any) => {
    content.value = value

    if (content.value !== undefined) {
      const theme = siteContent.value.settings?.theme
      const pageName = content.value.title
      document.title = pageName + ' - ' + theme
      setBreadcrumbs()
    }
  }

  const setApiUrl = (url: string | undefined) => {
    apiUrl.value = url ? url : ''
  }

  const setAppSpinner = (bool: boolean) => {
    showAppSpinner.value = bool
  }

  const getBrand = (): string => {
    const findMatchingURL = (url: string, searchTerms: string[]): string | null => {
      for (const searchTerm of searchTerms) {
        if (url.includes(searchTerm)) {
          return searchTerm === 'danskerhvervspsykologi' ? 'dep' : searchTerm
        }
      }

      return null
    }

    const url = window.location.hostname
    // Remember on Native - that we will hit the fallback because the window.location.hostname is not defined
    const brands = ['as3', 'a2b', 'danskerhvervspsykologi', 'dep']

    const matchingURL = findMatchingURL(url, brands)

    if (matchingURL) {
      console.log('Matching URL:', matchingURL)
      return matchingURL
    } else {
      console.log('No matching URLs found. Return theme as brand')
      return theme.value === Theme.BASE ? 'as3' : theme.value.toLowerCase()
    }
  }

  const resetAppStore = () => {
    content.value = undefined
    showLanguageSelector.value = false
    showNotificationCenter.value = false
    navigationBetweenModules.value = false
    resetScrollToItem()
    showValidationPage.value = false
  }

  const setBreadcrumbs = () => {
    breadcrumbs.value = []
    const parent = content.value.parent

    // types where we want to exclude the 'middle' section of breadcrumbs
    const excludeTypes = ['documentssection', 'articleformoverview', 'profile', 'accountsettings']

    breadcrumbs.value.unshift({ titleProperty: content.value.title })

    if (
      parent != undefined &&
      parent.url !== '/' &&
      excludeTypes.includes(content.value.type) === false
    ) {
      breadcrumbs.value.unshift({ titleProperty: parent.title, route: parent.url })
    }

    // Home is not shown - just to indicate what it is
    breadcrumbs.value.unshift({ titleProperty: 'Home', route: '/' })
  }

  const setNativeLinkIsAnotherLanguage = (obj: INativeWrongLang | undefined) => {
    nativeLinkIsAnotherLanguage.value = obj
  }

  const setShowNativeNavWarning = (toggle: boolean) => {
    showNativeNavWarning.value = toggle
  }

  const setDeepLinkSettings = (deepLinkSetting: DeepLinkSettings) => {
    deepLinkSettings.value = deepLinkSetting
  }

  const setIsDeeplink = (bool: boolean) => {
    isDeeplink.value = bool
  }

  const setScrollHitsElement = (hit: boolean) => {
    scrollHitsElement.value = hit
  }

  const setCallGetContent = (bool: boolean) => {
    callGetContent.value = bool
  }

  const setShowValidationPage = (show: boolean) => {
    showValidationPage.value = show
  }

  const isProd = (): boolean => {
    return import.meta.env.VITE_ENVIRONMENT === 'prod'
  }

  const setInnerWidth = () => {
    innerWidth.value = window.innerWidth
  }

  const setCanCloseApp = (canClose: boolean) => {
    canCloseApp.value = canClose
  }

  return {
    initialized,
    setInitialized,
    showContentSpinner,
    getContent,
    content,
    setContent,
    apiUrl,
    contentStatus,
    setContentSpinner,
    siteContent,
    setContentStatus,
    getSiteContent,
    setApiUrl,
    showLanguageSelector,
    showAppSpinner,
    setAppSpinner,
    getBrand,
    navigationBetweenModules,
    resetAppStore,
    setBreadcrumbs,
    breadcrumbs,
    nativeLinkIsAnotherLanguage,
    setNativeLinkIsAnotherLanguage,
    setShowNativeNavWarning,
    showNativeNavWarning,
    setDeepLinkSettings,
    deepLinkSettings,
    isDeeplink,
    setIsDeeplink,
    setScrollHitsElement,
    scrollHitsElement,
    setShowValidationPage,
    showValidationPage,
    callGetContent,
    setCallGetContent,
    isProd,
    candidateMessages,
    getCandidateMessages,
    innerWidth,
    setInnerWidth,
    showNotificationCenter,
    canCloseApp,
    setCanCloseApp
  }
})
